import * as React from 'react';
import MDialog from '../../../components/controls/MDialog';
import { Box, Button, Grid, InputLabel, TextField, CircularProgress, Checkbox, Skeleton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { DataGrid } from '@mui/x-data-grid';
import { useNotify } from 'react-admin';
import MRadio from '../../../components/controls/MRadio';
import MSelect from '../../../components/controls/MSelect';
import { ExchangeMarket } from '../../../utils/enum';
import MNumber from '../../../components/controls/MNumber';
import { REMOTE_API, getDataUpstream, objectToFormData } from '../utils';
import { IChoice, IListingVariable, ISource, ISourceVariable, IUpstreamRow } from '../types';

/** Props for {@link Item}, this file's label/field layout row. */
interface IItemProps {
    /** Field control(s) rendered in the right column. */
    children: React.ReactNode;
    /** Label rendered in the left column. */
    label: string;
    /** Top margin applied to the label. Defaults to `3`. */
    marginTop?: number;
}

const Item: React.FC<IItemProps> = ({ children, label, marginTop }) => {
    return (
        <Grid container spacing={5} size={12}>
            <Grid size={2} sx={{ textAlign: 'right' }}>
                {/* eslint-disable-next-line eqeqeq -- marginTop is typed number | undefined; == null intentionally matches both */}
                <InputLabel id={label} sx={{ marginTop: marginTop != null ? marginTop : 3 }}>{label}</InputLabel>
            </Grid>
            <Grid size={10}>
                {children}
            </Grid>
        </Grid>
    );
};

/** Data forwarded to {@link SourceEditPopup} describing which source to edit. */
export interface ISourceEditPopupData {
    /** Whether the dialog is open. */
    isEdit: boolean;
    /** The source being edited. */
    source: ISource;
}

/** Props for {@link SourceEditPopup}. */
export interface ISourceEditPopupProps {
    /** Dialog data: whether it's open and which source to edit. */
    data: ISourceEditPopupData;
    /** Known market types, used to build the per-upstream rows. */
    marketType: IChoice[];
    /** Known `SourceVariable` records, used to compute pricer/rate variable names. */
    sourceVariable: ISourceVariable[];
    /** Data provider used to load/save the source and its `ListingVariable`s. */
    dataProvider: any;
    /** Called when the dialog is dismissed (cancelled or after a successful update). */
    handleClose: () => void;
}

/** {@link SourceEditPopup}'s "have we fetched the bulk `ListingVariable` list yet" tracking state. */
interface IConfigDialogState {
    /** Whether `ListingVariable/GetList` has already been called for the current rows. */
    isGetVariable: boolean;
}

/** {@link SourceEditPopup}'s local dialog/source-editing state. */
interface ISourceEditPopupState {
    /** Dialog title. */
    title: string;
    /** The source being edited, augmented with parsed upstream/market/autopricer fields. */
    source: ISource;
    /** Toggled whenever a dependent field changes, to re-trigger `setFormattedUrl`. */
    isChange: boolean;
    /** Whether the upstream table hasn't finished its first render yet. */
    isFirstLoadUpstream: boolean;
    /** Whether `loadUpstream` has already populated the rows from `source.Upstream`. */
    isLoadedUpstream: boolean;
    /** Whether an update request is in flight. */
    isLoading: boolean;
}

/**
 * Dialog for editing an existing CSV Merge source: upstream selection grid
 * (with per-row type/top/auto-pricer/rate config), URL/market/description
 * fields, and the update flow that persists the source plus its pricer
 * `ListingVariable`s.
 * @param props - `data` (`{ isEdit, source }`), `marketType`, `sourceVariable`, `dataProvider`, `handleClose`.
 */
const SourceEditPopup: React.FC<ISourceEditPopupProps> = ({ data, marketType, sourceVariable, dataProvider, handleClose }) => {
    const { isEdit, source } = data;
    const notify = useNotify();
    const [rows, setRows] = React.useState<IUpstreamRow[]>([]);
    const [configDialog, setConfigDialog] = React.useState<IConfigDialogState>({
        isGetVariable: false
    });
    const [state, setState] = React.useState<ISourceEditPopupState>({
        title: 'Edit source',
        source: source,
        isChange: false,
        isFirstLoadUpstream: true,
        isLoadedUpstream: false,
        isLoading: false // for update process
    });
    // base on AutoPricer checkbox then save it in ListingVariable, ex: "Account".pricer.active = 0/1
    const [pricerListingVariable, setPricerListingVariable] = React.useState<IListingVariable>();

    React.useEffect(() => {
        initUpstream();
    }, [marketType]);

    React.useEffect(() => {
        setState((prev) => ({ ...prev, source: { ...prev.source, ...getDataUpstream(source.Url), ListUpstream: [] } }));
        // Fetch one listingVariable
        const getOneListingVariable = async () => {
            try {
                let pricer = {
                    id: 0,
                    sm_market: source.Market,
                    market_name: source.Name,
                    var_name: `${source.Name}.pricer.active`
                } as IListingVariable;
                const response = await dataProvider.postData('ListingVariable', 'GetOne', pricer);
                if (response && response.data) {
                    pricer = response.data;
                }
                setPricerListingVariable(pricer);
            } catch (error) {
                notify(`Fetch pricer listingVariable Error: ${error}`, { type: 'error' });
            }
        };
        getOneListingVariable();
    }, [source]);

    React.useEffect(() => {
        if (rows.length && sourceVariable.length && source && !state.isLoadedUpstream) {
            loadUpstream();
        }
    }, [rows, state.isLoadedUpstream, sourceVariable.length]);

    React.useEffect(() => {
        if (!configDialog.isGetVariable && sourceVariable.length && rows.length) {
            let rowsChecked = rows.filter((x) => x.checked);
            // eslint-disable-next-line eqeqeq -- loose check intentionally matches both null and undefined
            let listVariable = rowsChecked.map((m) => m.variable.pricer).concat(rowsChecked.filter((x) => x.variable.rate.var_decimal_value != null).map((m) => m.variable.rate));
            const loadListingVariable = async () => {
                try {
                    const response = await dataProvider.postData('ListingVariable', 'GetList', listVariable);
                    if (response && response.data.length) {
                        updateRowDataByAutoPricer(true, response.data);
                    }
                } catch (error) {
                    setState((prev) => ({ ...prev, isLoading: false }));
                    notify(`Load rule error: ${error}`, { type: 'error' });
                }
            };
            loadListingVariable();
            setConfigDialog((prev) => ({ ...prev, isGetVariable: true }));
        }
    }, [sourceVariable.length, rows.length]);

    React.useEffect(() => {
        setFormattedUrl();
    }, [state.isChange]);

    function initUpstream () {
        let _newRows: IUpstreamRow[] = [];
        (marketType || []).map((_us: IChoice, index: number) => {
            let item = {
                id: index,
                checked: false,
                upstream: _us.name.toLowerCase(),
                type: '',
                top: '',
                formatted: '',
                rate: '',
                autopricer: false,
                options: { type: false, top: false },
                // Update tts-upstreams to allow apply VIVID_EL2.accesso.pricer.active for specific markets
                variable: { pricer: {}, rate: {} }
            };
            _newRows.push(item);
        });

        setRows(_newRows);
    }

    function loadUpstream () {
        let newSource = state.source.ListUpstream;
        if (state.source.Upstream) {
            let updatedRows = rows;
            let upstreams = source.Upstream?.split(',') || [];
            upstreams.forEach((_us) => {
                let _upstr = _us.split('.');
                // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                let index = updatedRows.findIndex(x => x.upstream == _upstr[0]);
                if (index >= 0) {
                    let item = updatedRows[index];
                    item.checked = true;
                    // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                    if (!newSource?.find((x) => x == _upstr[0])) {
                        newSource!.push(_upstr[0]);
                    }
                    if (_upstr[1]) {
                        if (_upstr[1].includes('top')) item.top = getTop(_upstr[1]);
                        else item.type = _upstr[1];
                    }
                    if (_upstr[2]) item.top = getTop(_upstr[2]);
                    item.formatted = getFormatted(item.checked, item.upstream, item.type, item.top);
                    item.options = { type: true, top: true };
                    // Load variable for pricer & rate
                    item.autopricer = state.source.AutoPricer ? true : false;
                    // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                    let realUpstream = sourceVariable.find((x) => x.id == item.upstream.toLowerCase());
                    if (realUpstream) {
                        let pricer_varname = `${state.source.Name}.${realUpstream.variableName}.pricer.active`;
                        let pricer_value = state.source.AutoPricer ? 1 : 0;
                        item.variable.pricer = {
                            id: 0,
                            sm_market: state.source.Market,
                            market_name: state.source.Name,
                            var_name: pricer_varname,
                            var_decimal_value: pricer_value
                        };
                        item.variable.rate = {
                            id: 0,
                            sm_market: state.source.Market,
                            market_name: state.source.Name,
                            var_name: `${state.source.Name}.${realUpstream.variableName}.pricer.rate`,
                            var_decimal_value: 0
                        };
                    }
                }
            });
            setRows(updatedRows);
        }
        let anothers = state.source.Anothers || '';
        // eslint-disable-next-line eqeqeq -- comparing string characters, loose equality kept for consistency with existing checks
        if (anothers[0] == '&') {
            anothers = anothers.slice(1, anothers.length);
        }
        anothers = anothers ? `?${anothers}` : '';
        setState((prev) => ({ ...prev, source: { ...prev.source, Url: `${state.source.BaseUrl}${anothers}` }, isChange: !prev.isChange, isLoadedUpstream: true, isFirstLoadUpstream: false }));

        function getTop (value: string) {
            return value.match(/(?:top)(\w+)/)?.[1] || '';
        }
    }

    function getFormatted (checked: boolean, upstream: string, type?: string, top?: string | number) {
        let formatted = '';
        if (checked) {
            formatted = `${upstream}${type ? '.' + type : ''}${top ? '.top' + top : ''}`;
        }
        return formatted;
    }

    function setFormattedUrl () {
        if (state.source.ListUpstream) {
            let market = state.source.Market;
            let autopricer = state.source.AutoPricer ? 1 : 0;
            let _upstreams = '';
            state.source.ListUpstream.forEach((upstr) => {
                // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                let formatted = rows.find((x) => x.upstream == upstr)?.formatted || '';
                _upstreams = _upstreams.concat(`${formatted},`);
            });
            _upstreams = _upstreams.slice(0, _upstreams.length - 1); // Remove last character
            _upstreams = _upstreams ? `upstream=${_upstreams}&` : '';
            market = market ? `market=${market}&` : '';
            let dataStream = `${_upstreams}${market}autopricer=${autopricer}`;
            dataStream = dataStream ? `?${dataStream}` : '';
            let another = state.source.Anothers;
            if (dataStream) {
                another = another ? `&${another}` : '';
            } else {
                another = another ? `?${another}` : '';
            }
            let formattedUrl = `${state.source.BaseUrl}${dataStream}`;
            // Remove & at end if exist
            // eslint-disable-next-line eqeqeq -- comparing string characters, loose equality kept for consistency with existing checks
            if (formattedUrl[formattedUrl.length - 1] == '&') {
                formattedUrl = formattedUrl.slice(0, formattedUrl.length - 1);
            }
            formattedUrl = `${formattedUrl}${another}`;
            setState((prev) => ({ ...prev, source: { ...prev.source, Formatted: formattedUrl } }));
        }
    }

    const handleChangeUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setState((prev) => ({ ...prev, source: { ...prev.source, ...getDataUpstream(value, prev.source.Formatted), Url: value }, isChange: !prev.isChange }));
    };

    const handleChecked = (event: React.ChangeEvent<HTMLInputElement>, row: IUpstreamRow) => {
        const { checked } = event.target;
        let newUpstreams: string[] = [];
        updateRowData((r) => {
            if (r.id === row.id) {
                r.checked = checked;
                r.options = { type: checked, top: checked };
                if (checked) {
                    // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                    if (!state.source.ListUpstream?.find((x) => x == row.upstream)) {
                        newUpstreams.push(r.upstream);
                    }
                    r.formatted = getFormatted(checked, r.upstream, r.type, r.top);
                } else {
                    // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                    let index = state.source.ListUpstream?.findIndex(x => x == row.upstream) || -1;
                    if (index >= 0) {
                        newUpstreams.splice(index, 1);
                    }
                    r.formatted = getFormatted(checked, r.upstream);
                }
            } else if (r.checked) {
                newUpstreams.push(r.upstream);
            }
        });
        setState((prev) => ({ ...prev, source: { ...prev.source, ListUpstream: newUpstreams } }));
    };

    const handleCheckAutoPricer = (event: React.ChangeEvent<HTMLInputElement>, row: IUpstreamRow) => {
        const { checked } = event.target;
        updateRowData((r) => {
            if (r.id === row.id && r.checked) {
                r.autopricer = checked;
                // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                let realUpstream = sourceVariable.find((x) => x.id == row.upstream.toLowerCase());
                if (realUpstream) {
                    let varname = `${state.source.Name}.${realUpstream.variableName}.pricer.active`;
                    let value = checked ? 1 : 0;
                    r.variable.pricer = { ...r.variable.pricer, var_name: varname, var_decimal_value: value };
                }
            }
        }, false);
    };

    const handleRate = (value: number | string, row: IUpstreamRow) => {
        updateRowData((r) => {
            if (r.id === row.id && r.autopricer) {
                // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                let realUpstream = sourceVariable.find((x) => x.id == row.upstream.toLowerCase());
                if (realUpstream) {
                    let varname = `${state.source.Name}.${realUpstream.variableName}.pricer.rate`;
                    r.variable.rate = { ...r.variable.rate, var_name: varname, var_decimal_value: value };
                }
            }
        }, false);
    };

    const handleTop = (value: number | string, row: IUpstreamRow) => {
        updateRowData((r) => {
            if (r.id === row.id) {
                r.top = value;
                r.formatted = getFormatted(r.checked, r.upstream, r.type, value);
            }
        });
    };

    const handleType = (value: string | number, row: IUpstreamRow) => {
        updateRowData((r) => {
            if (r.id === row.id) {
                r.type = String(value);
                r.formatted = getFormatted(r.checked, r.upstream, String(value), r.top);
            }
        });
    };

    const handleAutoPricer = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setState((prev) => ({ ...prev, source: { ...prev.source, AutoPricer: checked }, isChange: !prev.isChange }));
        updateRowDataByAutoPricer(checked);
    };

    function updateRowDataByAutoPricer (checked: boolean, data: IListingVariable[] = []) {
        updateRowData((r) => {
            if (r.id === r.id && r.checked) {
                if (checked) r.autopricer = checked;
                if (data.length) { // load autopricer & rate from DB
                    let existPricer = data.find((x) => x.var_name.includes(r.variable.pricer.var_name!) && r.variable.pricer.var_name!.includes('.pricer.active'));
                    if (existPricer) {
                        // eslint-disable-next-line eqeqeq -- var_decimal_value may be numeric or string from the API, loose comparison intentional
                        r.autopricer = existPricer.var_decimal_value == 1 ? true : false;
                        r.variable.pricer = existPricer;
                    } else {
                        r.autopricer = false;
                    }
                    let existRate = data.find((x) => x.var_name.includes(r.variable.rate.var_name!) && r.variable.rate.var_name!.includes('.pricer.rate'));
                    if (existRate) {
                        r.rate = existRate.var_decimal_value!;
                        r.variable.rate = existRate;
                    }
                } else {
                    // eslint-disable-next-line eqeqeq -- upstream ids may be string or number depending on source, loose comparison intentional
                    let realUpstream = sourceVariable.find((x) => x.id == r.upstream);
                    if (realUpstream) {
                        let varname = `${state.source.Name}.${realUpstream.variableName}.pricer.active`;
                        r.variable.pricer.var_name = varname;
                        r.variable.pricer.var_decimal_value = checked ? 1 : 0;
                        r.rate = r.variable.rate.var_decimal_value!;
                    }
                }
            }
        }, false);
    };

    const handleMarket = (value: string | number) => {
        setState((prev) => ({ ...prev, source: { ...prev.source, Market: String(value) }, isChange: !prev.isChange }));
    };

    function updateRowData (callback: (r: IUpstreamRow) => void, isChange: boolean = true) {
        const updatedRows = rows.map((r) => {
            callback(r);
            return r;
        });
        setRows(updatedRows);
        if (isChange) setState((prev) => ({ ...prev, isChange: !prev.isChange }));
    }

    const handleUpdate = () => {
        let newSource = {
            Url: state.source.Formatted,
            Type: state.source.Type,
            Name: state.source.Name,
            Description: state.source.Description,
            is_disabled: state.source.is_disabled,
            Id: state.source.Id
        };
        setState((prev) => ({ ...prev, isLoading: true }));
        dataProvider.postData(REMOTE_API, 'sources', objectToFormData(newSource))
            .then(function () {
                notify('Source was updated', { type: 'success' });
                // Update pricer listingVariable
                if (pricerListingVariable) {
                    pricerListingVariable.var_decimal_value = state.source.AutoPricer ? 1 : 0;
                    dataProvider.create('ListingVariable', { data: pricerListingVariable })
                        .then(function () {
                            notify('Updated AutoPricer', { type: 'success' });
                            // Update each rows
                            if (state.source.AutoPricer) {
                                let rowsChecked = rows.filter((x) => x.checked);
                                let listVariable = rowsChecked.map((m) => m.variable.pricer).concat(rowsChecked.map((m) => m.variable.rate));
                                dataProvider.postData('ListingVariable', 'UpdateListVariable', listVariable)
                                    .then(function () {
                                        handleClose();
                                        notify('ListingVariable was updated', { type: 'success' });
                                        setState((prev) => ({ ...prev, isLoading: false }));
                                    }).catch((error: any) => {
                                        notify(`ListingVariable Error: ${error}`, { type: 'error' });
                                        handleClose();
                                    });
                            } else {
                                handleClose();
                                setState((prev) => ({ ...prev, isLoading: false }));
                            }
                        }).catch((error: any) => {
                            notify(`Pricer ListingVariable Error: ${error}`, { type: 'error' });
                            handleClose();
                        });
                }
            }).catch((error: any) => {
                notify(`Update Error: ${error}`, { type: 'error' });
            });
    };

    return (
        <MDialog name='m_isOpen'
            open={isEdit}
            title={state.title}
            handleClose={handleClose}
            maxWidth="lg"
            action={<Button startIcon={state.isLoading ? <CircularProgress size={20} /> : <SendIcon />} onClick={handleUpdate} variant='outlined'>Update</Button>}
        >
            <Grid container spacing={2} sx={{ display: 'block' }}>
                <Item label={'Name'} marginTop={0}>
                    <InputLabel id="Name" >{state.source.Name}</InputLabel>
                </Item>
                <Item label={'Type'}>
                    <MRadio name={'Type'} value={state.source.Type}
                        choices={[{ id: 0, name: 'Vivid' }, { id: 1, name: 'TN Impoter' }, { id: 2, name: 'TN Uploader' }, { id: 13, name: 'GC Uploader' }]}
                        onChange={(value) => setState((prev) => ({ ...prev, source: { ...prev.source, Type: Number(value) } }))} />
                </Item>
                <Item label={'Base Url'}>
                    <TextField id='Url' name='Url' placeholder='Upstream url eg.. http://www.tickettoolshed.com/report...' value={state.source.Url} sx={{ width: '100%' }} onChange={handleChangeUrl} />
                </Item>

                <Item label={'Formatted'} marginTop={0}>
                    <InputLabel style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }} id="Formatted">{state.source.Formatted}</InputLabel>
                </Item>
                <Item label={'Market'}>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <MSelect name="Market" choices={ExchangeMarket} value={state.source.Market} onChange={handleMarket} />
                        </Grid>
                        <Grid container size={6}>
                            <Grid size={6} sx={{ textAlign: 'center', paddingTop: 3 }}>
                                <InputLabel>{'Auto Pricer'}</InputLabel>
                            </Grid>
                            <Grid size={6} sx={{ paddingTop: 2 }}>
                                <Checkbox name='AutoPricer' checked={state.source.AutoPricer} onChange={handleAutoPricer} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Item>
                <Item label={'Description'}>
                    <TextField id='Description' name='Description' sx={{ width: '100%' }} value={state.source.Description}
                        onChange={(event) => setState((prev) => ({ ...prev, source: { ...prev.source, Description: event.target.value } }))} />
                </Item>
                <Item label={'Disabled'} >
                    <Checkbox name='is_disabled' sx={{ marginTop: 1.5 }} checked={state.source.is_disabled}
                        onChange={(event) => setState((prev) => ({ ...prev, source: { ...prev.source, is_disabled: event.target.checked } }))} />
                </Item>
            </Grid>
            {state.isFirstLoadUpstream ? <Skeleton /> :
                <Box sx={{ maxHeight: 400, width: '100%', overflowY: 'auto', paddingLeft: 5 }}>
                    <DataGrid
                        autoHeight
                        hideFooter
                        rows={rows}
                        columns={[
                            {
                                field: 'checked', headerName: 'Checked', width: 100, renderCell: (params) => {
                                    return (
                                        <Checkbox checked={params.row.checked} onChange={(event) => handleChecked(event, params.row)} />
                                    );
                                }
                            },
                            {
                                field: 'upstream', headerName: 'Upstream', width: 150
                            },
                            {
                                field: 'type', headerName: 'Type', width: 150, renderCell: (params) => {
                                    return (
                                        <MSelect variant='outlined'
                                            value={params.row.type}
                                            disabled={!params.row.options.type}
                                            choices={[{ id: 'lm', name: 'lm' }, { id: 'range', name: 'range' }]}
                                            onChange={(event) => handleType(event, params.row)} />
                                    );
                                }
                            },
                            {
                                field: 'top', headerName: 'Top', width: 150, renderCell: (params) => {
                                    return (
                                        <MNumber value={params.row.top} disabled={!params.row.options.top} onChange={(value) => handleTop(value, params.row)} variant='outlined' />
                                    );
                                }
                            },
                            {
                                field: 'formatted', headerName: 'Formatted', width: 200
                            },
                            {
                                field: 'autopricer', headerName: 'Auto Pricer', width: 100, renderCell: (params) => {
                                    return (
                                        <Checkbox checked={params.row.autopricer} disabled={!params.row.checked || !state.source.AutoPricer} onChange={(event) => handleCheckAutoPricer(event, params.row)} />
                                    );
                                }
                            },
                            {
                                field: 'rate', headerName: 'Rate', width: 150, renderCell: (params) => {
                                    return (
                                        <MNumber
                                            variant='outlined'
                                            range={[1, 2]}
                                            value={params.row.rate}
                                            disabled={!params.row.checked || !params.row.autopricer || !state.source.AutoPricer}
                                            decimal={5}
                                            onChange={(value) => handleRate(value, params.row)}
                                        />
                                    );
                                }
                            }
                        ]}
                    />
                </Box>}
        </MDialog>
    );
};

export default SourceEditPopup;
